import type { MedplumClient } from "@medplum/core";
import type { Coverage } from "@medplum/fhirtypes";

import { fetchClientBillingQuestionnaireResponse } from "@/clients/services/client-billing";
import type { LoadClientBillingPaymentMethodResult } from "@/shared/client-billing-payment-method/types/billing-payment-method";
import { resolveBillingPaymentMethodFromIntake } from "@/shared/client-billing-payment-method/utils/resolve-billing-payment-method";
import { getDisplayStringAsync, joinReferenceString } from "@/utils/medplum";

/**
 * Loads the client's Intake payment method and maps it to the canonical billing payer kind.
 */
export async function loadClientBillingPaymentMethod(
  medplum: MedplumClient,
  clientId: string,
): Promise<LoadClientBillingPaymentMethodResult> {
  try {
    const [, questionnaireResponse] = await fetchClientBillingQuestionnaireResponse(
      medplum,
      clientId,
    );
    const resolved = resolveBillingPaymentMethodFromIntake(questionnaireResponse);
    if (resolved.kind === "undetermined") {
      return { ok: false, reason: "undetermined" };
    }
    return { ok: true, data: resolved };
  } catch (error) {
    const details = error instanceof Error ? error.message : "Unknown error";
    return { ok: false, reason: "load_failed", details };
  }
}

/**
 * Returns the display name for the client's primary active Coverage payor, if any.
 */
export async function loadPrimaryCoveragePayerDisplay(
  medplum: MedplumClient,
  clientId: string,
): Promise<string | undefined> {
  const coverages = await medplum.searchResources("Coverage", {
    beneficiary: joinReferenceString("Patient", clientId),
    status: "active",
  });

  const primary = coverages.find((coverage: Coverage) => coverage.order === 1) ?? coverages[0];
  if (!primary?.payor?.[0]) return undefined;

  const payor = primary.payor[0];
  const inlineDisplay = payor.display?.trim();
  if (inlineDisplay) return inlineDisplay;

  return getDisplayStringAsync(medplum, payor);
}
